import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useLanguage from '../hooks/useLanguage';

const { FiDatabase, FiUsers, FiGlobe, FiTrendingUp } = FiIcons;

const useAnimatedNumber = (target, duration = 2000, isInView = false) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const frameRef = useRef();

  useEffect(() => {
    if (!isInView || hasAnimated) return;

    const startTime = Date.now();
    const startValue = 0;
    let endValue = 0;
    let suffix = '';
    const targetStr = target.toString();

    if (targetStr.endsWith('+')) {
      suffix = '+';
      let numberPart = targetStr.slice(0, -1);
      if (numberPart.endsWith('K')) {
        suffix = 'K+';
        numberPart = numberPart.slice(0, -1);
        endValue = parseInt(numberPart, 10) * 1000;
      } else {
        endValue = parseInt(numberPart, 10);
      }
    } else if (targetStr.includes('%')) {
      endValue = parseInt(targetStr.replace(/[^0-9]/g, ''), 10);
      suffix = ' %';
    } else {
      endValue = parseInt(targetStr.replace(/[^0-9]/g, ''), 10);
    }

    if (isNaN(endValue)) endValue = 0;

    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutQuart);
      let displayValue;

      if (suffix === 'K+') {
        displayValue = Math.floor(currentValue / 1000) + 'K+';
      } else {
        displayValue = currentValue + suffix;
      }

      setCount(displayValue);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        setHasAnimated(true);
        setCount(target);
      }
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [isInView, hasAnimated, target, duration]);

  return count;
};

const StatItem = ({ stat, index, isInView }) => {
  const animatedValue = useAnimatedNumber(stat.number, 2500, isInView);
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <motion.div variants={itemVariants} className="text-center group" whileHover={{ y: -5 }}>
      <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700">
        <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg sm:rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
          <SafeIcon icon={stat.icon} className="text-lg sm:text-xl lg:text-2xl text-white" />
        </div>
        <motion.div className="text-2xl sm:text-3xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {animatedValue}
        </motion.div>
        <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-blue-600 dark:text-blue-400 mb-1 sm:mb-2">
          {stat.label}
        </h3>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
          {stat.description}
        </p>
      </div>
    </motion.div>
  );
};

const StatsSection = () => {
  const { t } = useLanguage();
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);
  const stats = [
    { icon: FiDatabase, number: '170+', label: t('repositories'), description: t('registered') },
    { icon: FiUsers, number: '100K+', label: t('researchers'), description: t('connected') },
    { icon: FiGlobe, number: '20+', label: t('departments'), description: t('covered') },
    { icon: FiTrendingUp, number: '100 %', label: t('openAccess'), description: t('knowledge') }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.3 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 bg-gray-50 dark:bg-gray-900/50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8"
        >
          {stats.map((stat, index) => (
            <StatItem key={index} stat={stat} index={index} isInView={isInView} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default StatsSection;